// src/features/system/admin/components/CreateScriptDialog.tsx
import React, { useState } from 'react';
import AppDialog from '@/shared/components/ui/AppDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createScriptApi, CreateScriptInput, Script } from '@/features/system/services/api/servicesApi';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  service: { id_service: number; service_name: string } | null;
}

export default function CreateScriptDialog({ open, onOpenChange, service }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Partial<CreateScriptInput>>({});
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setForm({}); setError(null); };

  const mutation = useMutation<Script, any, CreateScriptInput>({
    mutationFn: (data) => createScriptApi(data),
    onSuccess: () => {
      // Refresh services list so scripts appear under service expansion
      qc.invalidateQueries({ queryKey: ['admin', 'services'] });
      reset();
      onOpenChange(false);
    },
    onError: (e: any) => setError(e?.message || 'Failed to create script'),
  });

  const setField = <K extends keyof CreateScriptInput>(k: K, v: CreateScriptInput[K]) => {
    setForm(f => ({ ...f, [k]: v }));
  };

  const canSave = !!service && !!form.script_name && !!form.table_name && !mutation.isPending;

  const handleConfirm = () => {
    if (!canSave || !service) return;
    mutation.mutate({
      id_service : service.id_service,
      script_name: form.script_name!.trim(),
      table_name : form.table_name!.trim(),
    });
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(v)=>{ if(!v) reset(); onOpenChange(v); }}
      title={`New script${service ? ` for ${service.service_name}` : ''}`}
      description="Register a script for this service."
      size="sm"
      onConfirm={handleConfirm}
      confirmLabel={mutation.isPending ? 'Creating…' : 'Create script'}
      confirmDisabled={!canSave}
      isBusy={mutation.isPending}
    >
      {!service && (
        <p className="text-sm text-rose-400">No service context.</p>
      )}
      {error && <p className="text-sm text-rose-400">{error}</p>}
      {service && (
        <>
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-neutral-400">Service</label>
            <input
              type="text"
              value={`${service.service_name} (#${service.id_service})`}
              className="bg-neutral-800 rounded px-2 py-1 text-sm text-neutral-400"
              disabled
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-neutral-400">Script Name *</label>
            <input
              autoFocus
              type="text"
              className="bg-neutral-800 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
              value={form.script_name || ''}
              onChange={e => setField('script_name', e.target.value)}
              placeholder="my_script"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs uppercase tracking-wide text-neutral-400">Table Name *</label>
            <input
              type="text"
              className="bg-neutral-800 rounded px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-indigo-500"
              value={form.table_name || ''}
              onChange={e => setField('table_name', e.target.value)}
              placeholder="runs_my_script"
            />
          </div>
        </>
      )}
    </AppDialog>
  );
}
